import { Stack } from '@cmg/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import { InstitutionalIndicationOrderType } from '../../../../../../../../graphql';
import { useIndicationActivitySalesAndTradingContext } from '../../../../context/IndicationActivitySalesAndTradingContext';
import { IndicationSectionWithEditButtons } from '../../common/indication-section-edit-view/IndicationSectionWithEditButtons';
import IndicationSectionForm from '../../common/indication-section-form/IndicationSectionForm';
import { getInitialTouchedFields } from '../../common/indication-section-form/IndicationSectionForm.model';
import { PassIndicationDialog } from '../../common/PassIndicationDialog';
import {
  IndicationFormValues,
  validateIndicationSection,
} from '../../common/utils/IndicationSectionValidation';
import { usePassOnCoveredIndication } from '../common/pass-covered-indication-dialog/hooks/usePassOnCoveredIndication';
import { createInitialValues as createInitialValuesPass } from '../indication-section-create-view/IndicationSectionCreateView.model';
import { useUpdateCoveredIndication } from './hooks/useUpdateCoveredIndication';
import { createInitialValues } from './IndicationSectionEditView.model';

export type Props = Readonly<{
  onFinishEditing: () => void;
  displayMissingContacts: boolean;
  isInvestorContactRequired: boolean;
}>;

const EMPTY_ARRAY = [];
const IndicationSectionEditView: React.FC<Props> = ({
  onFinishEditing,
  displayMissingContacts,
  isInvestorContactRequired,
}) => {
  const {
    offeringId,
    coveredIndication,
    basicPublishedOffering,
    orderBookConfiguration,
    publishedOffering,
  } = useIndicationActivitySalesAndTradingContext();
  const offering = basicPublishedOffering!;
  const indication = coveredIndication!;

  const [isPassDialogOpen, setIsPassDialogOpen] = React.useState(false);

  const [updateCoveredIndication] = useUpdateCoveredIndication(
    offeringId,
    indication,
    onFinishEditing
  );

  const convertibleFilingTalkRanges = useConvertibleFilingTalkRanges(
    defaultTo(publishedOffering?.terms.filings, null)
  );

  const initialValues =
    indication.type === InstitutionalIndicationOrderType.Pass
      ? createInitialValuesPass(offering)
      : createInitialValues({
          indication,
          convertibleFilingTalkRanges,
          offeringType: offering.type,
          isIndicationPriceAsPercentageParEnabled:
            orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
        });

  const formik = useFormik<IndicationFormValues>({
    initialValues,
    validate: values =>
      validateIndicationSection({
        values,
        isLimitIndicationsOnlyEnabled: orderBookConfiguration.isLimitIndicationsOnlyEnabled,
        isInvestorContactRequired,
        offeringType: offering.type,
        displayMissingContacts,
        isIndicationPriceAsPercentageParEnabled:
          orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
        isOnlySharesUnitOnIndicationsEnabled:
          orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled,
      }),
    validateOnMount: true,
    onSubmit: updateCoveredIndication,
    initialTouched: getInitialTouchedFields({
      interestLevels: initialValues.interestLevels,
      offeringType: offering.type,
      isIndicationPriceAsPercentageParEnabled:
        orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
    }),
  });

  const { isSubmitting, handleSubmit } = formik;

  const isPassButtonVisible = indication.type !== InstitutionalIndicationOrderType.Pass;

  const [onPass, isPassing] = usePassOnCoveredIndication({
    offeringId,
    indicationId: indication.id,
    indicationStatus: indication.status,
    onClose: () => setIsPassDialogOpen(false),
    onFinishEditing,
  });

  return (
    <IndicationSectionWithEditButtons
      indicationStatus={indication.status}
      isPassButtonVisible={isPassButtonVisible}
      isPassButtonDisabled={isSubmitting}
      handlePass={() => setIsPassDialogOpen(true)}
      isSubmitting={isSubmitting}
      handleDiscard={onFinishEditing}
      handleSubmit={handleSubmit}
    >
      <Stack gap={1}>
        <FormikProvider value={formik}>
          <Form>
            <IndicationSectionForm
              filings={defaultTo(publishedOffering?.terms.filings, null)}
              securityType={defaultTo(publishedOffering?.security.type, null)}
              tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
              instruments={defaultTo(publishedOffering?.instruments, null)}
              issuerName={defaultTo(publishedOffering?.issuer.name, null)}
              offeringType={offering.type}
              canEnterTranche={indication.type === InstitutionalIndicationOrderType.Pass}
              pricingCurrencyCode={offering.pricingCurrencyCode}
              securityTypeDisplayName={offering.securityTypeDisplayName!}
              isLimitIndicationsOnlyEnabled={orderBookConfiguration.isLimitIndicationsOnlyEnabled}
              isOnlySharesUnitOnIndicationsEnabled={
                orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled
              }
              isIndicationPriceAsPercentageParEnabled={
                orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
              }
            />
          </Form>
        </FormikProvider>

        <PassIndicationDialog
          isOpen={isPassDialogOpen}
          isLoading={isPassing}
          onClose={() => setIsPassDialogOpen(false)}
          onPass={onPass}
        />
      </Stack>
    </IndicationSectionWithEditButtons>
  );
};

export default IndicationSectionEditView;
